import { useEffect, useState } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';

/**
 * Three-tier role model surfaced from the Cognito ID token's
 * `cognito:groups` claim. Matches the backend `UserRole` type.
 */
export type UserRole = 'volunteer' | 'lead_moderator' | 'admin';

/** Mapping from Cognito group names to UI roles. */
const ROLE_GROUP_NAMES: Record<UserRole, string> = {
  volunteer: 'Volunteers',
  lead_moderator: 'LeadModerators',
  admin: 'Admins',
};

/**
 * Pick the highest-tier role from a list of Cognito group names. Returns
 * `volunteer` when no role group is present (a freshly-confirmed user
 * before the PostConfirmation trigger has run, for instance).
 */
export function pickRoleFromGroups(groups: readonly string[]): UserRole {
  if (groups.includes(ROLE_GROUP_NAMES.admin)) return 'admin';
  if (groups.includes(ROLE_GROUP_NAMES.lead_moderator)) return 'lead_moderator';
  return 'volunteer';
}

/**
 * Normalise the various shapes Cognito uses to serialise the
 * `cognito:groups` claim. Sometimes it's an array, sometimes a
 * comma-separated string, sometimes a bracketed string.
 */
export function parseGroupsClaim(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.filter((g): g is string => typeof g === 'string');
  }
  if (typeof raw === 'string') {
    const trimmed = raw.replace(/^\[/, '').replace(/\]$/, '').trim();
    return trimmed.length ? trimmed.split(/[\s,]+/).filter(Boolean) : [];
  }
  return [];
}

const TOTP_ISSUER = 'Brisbane Pride Moderation';

let cachedPool: CognitoUserPool | null = null;

function getPool(): CognitoUserPool | null {
  if (!cachedPool) {
    // Read env lazily — tests set these via import.meta.env in test-setup.
    const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as string;
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
    if (!userPoolId || !clientId) {
      // Allow tests / dev builds without env vars to call read-only helpers
      // without throwing. The auth-required functions still fail loudly when
      // there's no pool to talk to.
      return null;
    }
    cachedPool = new CognitoUserPool({
      UserPoolId: userPoolId,
      ClientId: clientId,
    });
  }
  return cachedPool;
}

function requirePool(): CognitoUserPool {
  const pool = getPool();
  if (!pool) {
    throw new Error(
      'Cognito user pool is not configured. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID.',
    );
  }
  return pool;
}

/** Used by tests to reset module state. */
export function __resetPoolCache(): void {
  cachedPool = null;
}

// ---------------------------------------------------------------------------
// Sign-up
// ---------------------------------------------------------------------------

/**
 * Register a new user. The Cognito User Pool's PreSignUp trigger rejects
 * any address that does not end in `@brisbanepride.org.au`. The returned
 * CognitoUser is what `confirmSignUp` / `resendConfirmationCode` need.
 */
export function signUp(
  email: string,
  password: string,
  inviteToken?: string,
): Promise<CognitoUser> {
  const pool = requirePool();
  const attributes = [
    new CognitoUserAttribute({ Name: 'email', Value: email }),
  ];
  return new Promise((resolve, reject) => {
    // The 6th argument is `clientMetadata`, which the PreSignUp Lambda
    // reads to admit invite-bearing external emails. The SDK typings
    // omit it, so we cast through `unknown` to keep TypeScript happy.
    type SignUpCallback = (
      err: Error | undefined,
      result: { user: CognitoUser } | undefined,
    ) => void;
    const callback: SignUpCallback = (err, result) => {
      if (err || !result) {
        reject(err ?? new Error('Sign-up failed'));
        return;
      }
      resolve(result.user);
    };
    if (inviteToken) {
      (pool.signUp as unknown as (
        username: string,
        password: string,
        attrs: CognitoUserAttribute[],
        validation: CognitoUserAttribute[],
        callback: SignUpCallback,
        clientMetadata?: Record<string, string>,
      ) => void)(
        email,
        password,
        attributes,
        [],
        callback,
        { token: inviteToken },
      );
    } else {
      pool.signUp(email, password, attributes, [], callback);
    }
  });
}

export function confirmSignUp(email: string, code: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: requirePool() });
  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, true, (err) => {
      if (err) reject(err);
      else resolve();
    });
  });
}

export function resendConfirmationCode(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: requirePool() });
  return new Promise((resolve, reject) => {
    user.resendConfirmationCode((err) => {
      if (err) reject(err);
      else resolve();
    });
  });
}

// ---------------------------------------------------------------------------
// Sign-in (with MFA + new-password support)
// ---------------------------------------------------------------------------

export type SignInResult =
  | { kind: 'authenticated'; user: CognitoUser }
  | { kind: 'newPasswordRequired'; user: CognitoUser; userAttributes: Record<string, unknown>; requiredAttributes: string[] }
  | { kind: 'mfaSetupRequired'; user: CognitoUser }
  | { kind: 'mfaCodeRequired'; user: CognitoUser };

/**
 * Begin sign-in. The returned state describes whether further user
 * interaction is needed (TOTP code, MFA setup, new-password challenge).
 */
export function signIn(email: string, password: string): Promise<SignInResult> {
  const pool = requirePool();
  const user = new CognitoUser({ Username: email, Pool: pool });
  const authDetails = new AuthenticationDetails({
    Username: email,
    Password: password,
  });

  return new Promise<SignInResult>((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: () => {
        resolve({ kind: 'authenticated', user });
      },
      onFailure: (err) => {
        reject(err);
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // Cognito injects the email into userAttributes for new-password flow.
        // It also asks the caller to drop attributes Cognito provides itself.
        delete userAttributes.email_verified;
        resolve({ kind: 'newPasswordRequired', user, userAttributes, requiredAttributes });
      },
      mfaSetup: () => {
        resolve({ kind: 'mfaSetupRequired', user });
      },
      totpRequired: () => {
        resolve({ kind: 'mfaCodeRequired', user });
      },
    });
  });
}

export function completeNewPassword(
  user: CognitoUser,
  newPassword: string,
  attributes: Record<string, unknown> = {},
): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, attributes, {
      onSuccess: () => resolve({ kind: 'authenticated', user }),
      onFailure: (err) => reject(err),
      mfaSetup: () => resolve({ kind: 'mfaSetupRequired', user }),
      totpRequired: () => resolve({ kind: 'mfaCodeRequired', user }),
    });
  });
}

// ---------------------------------------------------------------------------
// MFA: TOTP setup
// ---------------------------------------------------------------------------

/**
 * Build the standard `otpauth://` URI from a Cognito-issued TOTP secret
 * and an account identifier (the user's email). Authenticator apps such
 * as 1Password, Authy, and Google Authenticator scan this URI as a QR.
 */
export function buildTotpUri(secret: string, account: string): string {
  const label = encodeURIComponent(`${TOTP_ISSUER}:${account}`);
  const issuer = encodeURIComponent(TOTP_ISSUER);
  return `otpauth://totp/${label}?secret=${secret}&issuer=${issuer}&algorithm=SHA1&digits=6&period=30`;
}

/** Initiate TOTP enrolment; returns the shared secret to display as a QR. */
export function startMfaSetup(user: CognitoUser): Promise<{ secret: string; uri: string; account: string }> {
  return new Promise((resolve, reject) => {
    user.associateSoftwareToken({
      associateSecretCode: (secret) => {
        const account = user.getUsername();
        resolve({ secret, uri: buildTotpUri(secret, account), account });
      },
      onFailure: (err) => reject(err),
    });
  });
}

/** Confirm TOTP enrolment; the Cognito user is signed in once this resolves. */
export function verifyMfaSetup(user: CognitoUser, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    user.verifySoftwareToken(code, 'Brisbane Pride moderation device', {
      onSuccess: () => {
        // Phase 7 — the user pool runs at MfaConfiguration: OPTIONAL
        // (required for WebAuthn first-factor compatibility), so
        // `verifySoftwareToken` does NOT auto-flip the user's MFA
        // preference. Set it explicitly here so the next sign-in
        // gets a TOTP challenge.
        type MfaPreferenceFn = (
          smsSettings: unknown,
          softwareTokenSettings: { PreferredMfa: boolean; Enabled: boolean },
          callback: (err: Error | undefined) => void,
        ) => void;
        const preferenceFn = (user as unknown as {
          setUserMfaPreference?: MfaPreferenceFn;
        }).setUserMfaPreference;
        if (typeof preferenceFn !== 'function') {
          // Older SDK shapes: still resolve so the verifyMfaSetup
          // contract holds. The user's MFA preference will be set
          // the next time they pass through the flow.
          resolve();
          return;
        }
        preferenceFn.call(
          user,
          null,
          { PreferredMfa: true, Enabled: true },
          (err: Error | undefined) => {
            if (err) {
              reject(err);
              return;
            }
            resolve();
          },
        );
      },
      onFailure: (err) => reject(err),
    });
  });
}

// ---------------------------------------------------------------------------
// MFA: TOTP challenge on returning sign-ins
// ---------------------------------------------------------------------------

export function submitMfaCode(
  user: CognitoUser,
  code: string,
  /**
   * F-310 — when true, call setDeviceStatusRemembered after a
   * successful MFA challenge so Cognito skips MFA on subsequent
   * sign-ins from the same browser. Best-effort: any failure (e.g.
   * the User Pool has device tracking disabled) is non-fatal so the
   * primary sign-in path is unaffected.
   */
  trustDevice = false,
): Promise<void> {
  return new Promise((resolve, reject) => {
    user.sendMFACode(
      code,
      {
        onSuccess: () => {
          if (trustDevice) {
            try {
              const u = user as unknown as {
                setDeviceStatusRemembered?: (cb: {
                  onSuccess: () => void;
                  onFailure: (e: Error) => void;
                }) => void;
              };
              u.setDeviceStatusRemembered?.({
                onSuccess: () => {},
                onFailure: () => {},
              });
            } catch {
              // Non-fatal -- device tracking may be disabled.
            }
          }
          resolve();
        },
        onFailure: (err) => reject(err),
      },
      'SOFTWARE_TOKEN_MFA',
    );
  });
}

/**
 * Begin a backup-code sign-in. The companion Cognito DefineAuthChallenge
 * trigger sees `clientMetadata.signInWith === 'backup_code'` and issues
 * a CUSTOM_CHALLENGE, which `sendCustomChallengeAnswer(code)` then
 * answers. The verify-auth-challenge trigger runs bcrypt.compare against
 * the user's stored hashes; on a match Cognito issues tokens and the
 * user is signed in.
 *
 * The amazon-cognito-identity-js typings don't surface `customAuth` /
 * `clientMetadata` cleanly on the AuthenticationDetails class, so we
 * pass through `unknown` to keep TypeScript honest without rewriting
 * the SDK's surface.
 */
export function signInWithBackupCode(
  email: string,
  code: string,
): Promise<void> {
  const pool = requirePool();
  const user = new CognitoUser({ Username: email, Pool: pool });
  user.setAuthenticationFlowType('CUSTOM_AUTH');
  const authDetails = new AuthenticationDetails({
    Username: email,
    // No password — CUSTOM_AUTH bypasses SRP.
    ClientMetadata: { signInWith: 'backup_code' },
  } as unknown as ConstructorParameters<typeof AuthenticationDetails>[0]);
  return new Promise<void>((resolve, reject) => {
    user.initiateAuth(authDetails, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
      customChallenge: () => {
        user.sendCustomChallengeAnswer(
          code,
          {
            onSuccess: () => resolve(),
            onFailure: (err) => reject(err),
          },
          { signInWith: 'backup_code' },
        );
      },
    });
  });
}

// ---------------------------------------------------------------------------
// Password reset (Cognito ForgotPassword / ConfirmForgotPassword)
// ---------------------------------------------------------------------------

/** Begin a password-reset flow. Cognito emails a verification code. */
export function forgotPassword(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: requirePool() });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err: Error) => reject(err),
    });
  });
}

/**
 * Confirm a password reset with the verification code Cognito emailed.
 * On success the user can sign in with `newPassword`.
 */
export function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: requirePool() });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err: Error) => reject(err),
    });
  });
}

// ---------------------------------------------------------------------------
// Backup MFA codes
// ---------------------------------------------------------------------------

/**
 * Generate `count` random backup MFA codes, each 8 alphanumeric characters.
 * Shown once on TOTP setup so the moderator can paper-store them.
 *
 * Note (Phase 4 scope): we ship the generation + display surface here. The
 * consume path (verifying a backup code in place of a TOTP at sign-in)
 * requires custom Cognito Lambda triggers (DefineAuthChallenge +
 * VerifyAuthChallengeResponse). That is intentionally a follow-up — shipping
 * a broken consume path would be worse than the current state.
 */
export function generateBackupCodes(count = 10): string[] {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // omit easily-confused chars
  const buf = new Uint8Array(count * 8);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(buf);
  } else {
    for (let i = 0; i < buf.length; i++) buf[i] = Math.floor(Math.random() * 256);
  }
  const codes: string[] = [];
  for (let i = 0; i < count; i++) {
    let code = '';
    for (let j = 0; j < 8; j++) {
      code += alphabet[buf[i * 8 + j] % alphabet.length];
    }
    codes.push(code);
  }
  return codes;
}

// ---------------------------------------------------------------------------
// Session management (unchanged)
// ---------------------------------------------------------------------------

export function signOut(): void {
  const pool = getPool();
  if (!pool) return;
  const user = pool.getCurrentUser();
  if (user) {
    user.signOut();
  }
}

export interface CurrentUser {
  email: string;
  sub: string;
  groups: string[];
  role: UserRole;
}

export function getCurrentUser(): CurrentUser | null {
  const pool = getPool();
  if (!pool) return null;
  const user = pool.getCurrentUser();
  if (!user) return null;
  let session: CognitoUserSession | null = null;
  user.getSession((err: Error | null, s: CognitoUserSession | null) => {
    if (!err && s) session = s;
  });
  if (!session) return null;
  try {
    const payload = (session as CognitoUserSession).getIdToken().decodePayload();
    const groups = parseGroupsClaim(payload['cognito:groups']);
    return {
      email: (payload.email as string) || (payload['cognito:username'] as string) || '',
      sub: (payload.sub as string) || '',
      groups,
      role: pickRoleFromGroups(groups),
    };
  } catch {
    return null;
  }
}

/**
 * React hook returning the current authenticated user, including their
 * derived role. Returns null when not signed in.
 *
 * The Cognito SDK does not emit auth events, so this hook simply reads
 * once on mount; components that need to refresh (after MFA setup, for
 * example) can call `getCurrentUser()` directly.
 */
export function useCurrentUser(): CurrentUser | null {
  const [user, setUser] = useState<CurrentUser | null>(() => getCurrentUser());
  useEffect(() => {
    setUser(getCurrentUser());
  }, []);
  return user;
}

export function getIdToken(): Promise<string | null> {
  const pool = getPool();
  if (!pool) return Promise.resolve(null);
  const user = pool.getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

/**
 * Returns the Cognito access token for the currently signed-in user, or
 * null if no valid session exists. Used by the WebAuthn helpers to call
 * `ListWebAuthnCredentials`, `StartWebAuthnRegistration`,
 * `CompleteWebAuthnRegistration`, and `DeleteWebAuthnCredential` against
 * the Cognito service, all of which authenticate with an access token
 * rather than the ID token used by API Gateway.
 */
export function getAccessToken(): Promise<string | null> {
  const pool = getPool();
  if (!pool) return Promise.resolve(null);
  const user = pool.getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(session.getAccessToken().getJwtToken());
    });
  });
}

export function isAuthenticated(): boolean {
  const pool = getPool();
  if (!pool) return false;
  const user = pool.getCurrentUser();
  if (!user) return false;
  let valid = false;
  user.getSession((err: Error | null, session: CognitoUserSession | null) => {
    if (!err && session && session.isValid()) {
      valid = true;
    }
  });
  return valid;
}
